import { Minus, Plus } from 'lucide-react';

/**
 * Quantity stepper — BUILD-PLAN §9, §27, §40, §44.
 *
 * Steppers are a common accessibility failure, so the details here matter:
 *   • the value is a real <input type="number">, so it can be typed, pasted
 *     and read by assistive technology — not a <div> with two buttons
 *   • buttons are 44px targets and are disabled at the bounds rather than
 *     silently doing nothing (§86)
 *   • the stock ceiling comes from the server; the input is clamped so the
 *     customer cannot ask for more than exists
 */

interface QuantitySelectorProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  /** Upper bound, typically available stock. */
  max?: number;
  disabled?: boolean;
}

export function QuantitySelector({
  value,
  onChange,
  min = 1,
  max = 99,
  disabled = false,
}: QuantitySelectorProps) {
  const clamp = (next: number) => Math.min(max, Math.max(min, next));

  return (
    <div className="inline-flex items-stretch border border-line">
      <Step
        label="Decrease quantity"
        onClick={() => onChange(clamp(value - 1))}
        disabled={disabled || value <= min}
      >
        <Minus className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
      </Step>

      <input
        type="number"
        inputMode="numeric"
        aria-label="Quantity"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(event) => {
          const parsed = Number.parseInt(event.target.value, 10);

          // Ignore an empty or half-typed value rather than snapping the
          // field back to 1 while the customer is still typing.
          if (!Number.isNaN(parsed)) {
            onChange(clamp(parsed));
          }
        }}
        className="w-14 border-x border-line bg-surface text-center text-sm text-ink [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      />

      <Step
        label="Increase quantity"
        onClick={() => onChange(clamp(value + 1))}
        disabled={disabled || value >= max}
      >
        <Plus className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
      </Step>
    </div>
  );
}

function Step({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex h-11 w-11 items-center justify-center text-ink transition-colors hover:text-accent disabled:cursor-not-allowed disabled:opacity-35"
    >
      {children}
    </button>
  );
}
